import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { PayPeriod } from '@app/interfaces/pay-period.interface';
import { JournalEntryReportPayPeriodFilterOption } from '@app/models/payroll/journal-entry-report-pay-period-filter-option.model';
import {
    JournalEntryAdditionalReportFields,
    VacationYTDBalancesReportAdditionalFields,
} from '@app/modules/reporting/interfaces/additional-report-fields.interface';
import { BasePayrollReport } from '@app/modules/reporting/interfaces/report.interface';
import { BaseForm } from '@forms/base.form';
import { Schedule } from '@models/payroll/schedule.model';
import { Report } from '@reporting/services/queries/report';
import {
    endOfMonth,
    endOfQuarter,
    endOfYear,
    getMonth,
    getQuarter,
    isSameDay,
    isSameMonth,
    isSameQuarter,
    isToday,
    startOfMonth,
    startOfQuarter,
    startOfYear,
} from 'date-fns';
import moment from 'moment';

@Component({
    selector: 'app-form-filter-payroll-report',
    templateUrl: './filter-payroll-report.template.html',
    styleUrls: ['./filter-payroll-report.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterPayrollReportForm extends BaseForm implements OnInit {
    @Input() report:
        | BasePayrollReport
        | Report<JournalEntryAdditionalReportFields>
        | Report<VacationYTDBalancesReportAdditionalFields>
        | null = null;
    @Input() reportStartAt: Date | null = null;
    @Input() reportEndAt: Date | null = null;
    @Input() isJournalEntryV2Report = false;
    @Input() journalEntryReportPayPeriodOptions: JournalEntryReportPayPeriodFilterOption[] = [];
    @Input() allPeriodProperty = 'allPeriods';
    @Input() displayPayPeriodName = false;
    @Input() shouldShowFilterBy = true;
    @Input() jurisdictionFilter = false;
    @Input() jurisdictions: { label: string; value: string }[];
    @Input() jurisdiction: string;
    @Input() schedule: Schedule | null = null;
    @Input() showAllTimeOption = false;
    @Input() initialFilter?: string;
    @Output() journalEntryReportPayPeriodOptionsYearEmitter: EventEmitter<string> = new EventEmitter<string>();
    filter = 'annual';
    year: number = new Date().getFullYear();
    quarter: number | null = null;
    month: number | null = null;
    period: PayPeriod | null = null;
    includePending = true;
    selectedJournalEntryReportPayPeriod: JournalEntryReportPayPeriodFilterOption | null = null;
    payPeriodName = '';
    typesWithoutPrefix = ['OffCycle', 'YearEndAdjustment'];

    dates: any = {
        startAt: Date.now(),
        endAt: Date.now(),
    };

    readonly monthMap = [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december',
    ];

    get filterHasBeenSet(): boolean {
        return Boolean(this.year || this.quarter || this.month || this.month === 0 || this.period);
    }

    constructor(
        private element: ElementRef,
        private changeDetector: ChangeDetectorRef
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        if (this.initialFilter) {
            this.filter = this.initialFilter;
        }
        if (this.schedule) {
            this.schedule.reverse(this.allPeriodProperty);
            this.changeDetector.detectChanges();
            this.setDefaultYear();
            this.journalEntryReportPayPeriodOptionsYearEmitter.emit(this.year.toString());
            this.beforeShow();
        }
    }

    setDefaultYear(): void {
        if (!this.schedule) {
            return;
        }

        const lastPaidPeriod = this.schedule.lastPaidPeriod;
        if (!lastPaidPeriod) {
            return;
        }
        this.year = lastPaidPeriod.year ? lastPaidPeriod.year : moment(lastPaidPeriod.chequeDate).year();
    }

    beforeShow(): void {
        this.setFilterToDates(this.reportStartAt, this.reportEndAt);
        this.updateYearDropdown();
    }

    /**
     * Try to set the filter values based on dates passed in
     * If the filter values have already been set, do nothing
     */
    setFilterToDates(startAt: Date, endAt: Date): void {
        if (this.filterHasBeenSet) {
            return;
        }

        // Matches are checked (and overridden) from least specific to most specific
        this.setFilterIfDatesMatchAnnual(startAt, endAt);
        this.setFilterIfDatesMatchQuarter(startAt, endAt);
        this.setFilterIfDatesMatchMonth(startAt, endAt);
        this.setFilterIfDatesMatchPeriod(startAt, endAt);
        this.calculateDateRange();
    }

    calculateDateRange(filter: string | null = null): void {
        const year = this.getSelectYear();

        if (this.isJournalEntryV2Report && filter === 'year') {
            this.journalEntryReportPayPeriodOptionsYearEmitter.emit(this.year.toString());
        }

        switch (this.filter) {
            case 'annual':
                this.month = null;
                this.quarter = null;
                this.period = null;
                this.dates.startAt = moment({ year: this.year }).startOf('year');
                this.dates.endAt = moment({ year: this.year }).endOf('year');
                if (this.displayPayPeriodName) {
                    this.payPeriodName = '';
                }
                break;
            case 'quarter':
                this.month = null;
                this.period = null;
                this.dates.startAt = moment({ year: this.year }).quarter(this.quarter).startOf('quarter');
                this.dates.endAt = moment({ year: this.year }).quarter(this.quarter).endOf('quarter');
                if (this.displayPayPeriodName) {
                    this.payPeriodName = '';
                }
                break;
            case 'month':
                this.quarter = null;
                this.period = null;
                this.dates.startAt = moment({ year: this.year, month: this.month - 1 }).startOf('month');
                this.dates.endAt = moment({ year: this.year, month: this.month - 1 }).endOf('month');
                if (this.displayPayPeriodName) {
                    this.payPeriodName = '';
                }
                break;
            case 'payPeriods':
                this.month = null;
                this.quarter = null;

                // If the user has changed the year,
                // the selected period may no longer be applicable
                {
                    const selectedPeriodIsInSelectedYear = year.payPeriods.some(
                        (payPeriod: PayPeriod) => payPeriod === this.period
                    );

                    if (!selectedPeriodIsInSelectedYear) {
                        this.period = year.payPeriods[0];
                    }
                }

                this.dates.startAt = moment(this.period.chequeDate).startOf('day');
                this.dates.endAt = moment(this.period.chequeDate).endOf('day');
                if (this.displayPayPeriodName) {
                    this.payPeriodName = this.payPeriodLabel(this.period);
                }
                break;
            case 'allTime':
                this.month = null;
                this.quarter = null;
                this.period = null;
                this.dates.startAt = moment({ year: this.schedule?.allPeriods[0].year }).startOf('year');
                this.dates.endAt = moment({
                    year: this.schedule?.allPeriods[this.schedule?.allPeriods.length - 1].year,
                }).endOf('year');
                if (this.displayPayPeriodName) {
                    this.payPeriodName = '';
                }
                break;
            default:
                this.dates.startAt = moment();
                this.dates.endAt = moment();
                if (this.displayPayPeriodName) {
                    this.payPeriodName = '';
                }
                break;
        }

        this.dates.startAt = this.dates.startAt.format('YYYY-MM-DD');
        this.dates.endAt = this.dates.endAt.format('YYYY-MM-DD');
        this.changeDetector.detectChanges();
    }

    getSelectYear(): any {
        const year = this.schedule[this.allPeriodProperty].find((period: PayPeriod) => period.year == this.year);
        if (!year) {
            return;
        }

        year.payPeriods = year.payPeriods.filter(
            ({ payrollState, type }) => payrollState && payrollState !== 'open' && type !== 'Stub'
        );
        return year;
    }

    payPeriodLabel(payPeriod: PayPeriod): string {
        if (payPeriod.type === 'OffCycle') {
            return 'components.form-filter-payroll-report.offCycle';
        }

        if (payPeriod.type === 'Adjustment') {
            return 'components.form-filter-payroll-report.adjustment';
        }

        if (payPeriod.type === 'YearEndAdjustment') {
            return 'components.form-filter-payroll-report.yearEndAdjustment';
        }

        return '';
    }

    private setFilterIfDatesMatchAnnual(startAt: Date, endAt: Date): void {
        const isNewYearsDay = isSameDay(startAt, startOfYear(startAt));
        const endsToday = isToday(endAt);
        const isNewYearsEve = isSameDay(endAt, endOfYear(endAt));

        const matchesAnnual = isNewYearsDay && (endsToday || isNewYearsEve);

        if (!matchesAnnual) {
            return;
        }

        this.year = startAt.getFullYear();
        this.filter = 'annual';
    }

    private setFilterIfDatesMatchQuarter(startAt: Date, endAt: Date): void {
        const isStartOfQuarter = isSameDay(startAt, startOfQuarter(startAt));
        const isEndOfQuarter = isSameDay(endAt, endOfQuarter(endAt));
        if (!(isStartOfQuarter && isEndOfQuarter && isSameQuarter(startAt, endAt))) {
            return;
        }

        this.year = startAt.getFullYear();
        this.quarter = getQuarter(startAt);
        this.filter = 'quarter';
    }

    private setFilterIfDatesMatchMonth(startAt: Date, endAt: Date): void {
        const isStartOfMonth = isSameDay(startAt, startOfMonth(startAt));
        const isEndOfMonth = isSameDay(endAt, endOfMonth(endAt));

        if (!(isStartOfMonth && isEndOfMonth && isSameMonth(startAt, endAt))) {
            return;
        }

        this.year = startAt.getFullYear();
        this.month = getMonth(startAt);
        this.filter = 'month';
    }

    private setFilterIfDatesMatchPeriod(startAt: Date, endAt: Date): void {
        const matchingPeriod = this.schedule.getPeriodMatchingDates(startAt, endAt, this.allPeriodProperty);

        if (!matchingPeriod) {
            return;
        }

        this.year = startAt.getFullYear();
        this.period = matchingPeriod;
        this.filter = 'payPeriods';
    }

    private updateYearDropdown(): void {
        if (!this.year) {
            return;
        }

        setTimeout(() => {
            const dropdownElement = $(this.element.nativeElement).find('.year .ui.dropdown');
            $(dropdownElement).dropdown('set selected', this.year);
        });
    }
}
